import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Router, NavigationEnd, NavigationStart, NavigationCancel, NavigationError, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Sidenavigation } from '../shared/layouts/sidenavigation/sidenavigation';
import { RoleService } from '../shared/services/role.service';
import { ApiService } from '../shared/services/api.service';
import { TuiLoader } from '@taiga-ui/core';
import { ChangeDetectorRef } from '@angular/core';
import { Subscription, skip } from 'rxjs';
import { PlatformService } from '../shared/native/platform.service';

@Component({
  selector: 'app-layout-wrapper',
  standalone: true,
  imports: [CommonModule, RouterOutlet, Sidenavigation, TuiLoader],
  templateUrl: './layout-wrapper.html',
  styleUrls: ['./layout-wrapper.scss'],
  providers: [{ provide: 'TUI_LOADER_OPTIONS', useValue: { size: 'xl' } }] // Set default size for all loaders in this component
})
export class LayoutWrapper implements OnInit, OnDestroy {
  /** Phone app uses the POS header. The desktop side nav stays off until a page actually needs it. */
  readonly nativeShell = inject(PlatformService).isNative;
  hideNavigation = this.nativeShell;
  isLoadingOverlay = false;
  loadingText = 'Loading...';
  
  // Debug flag to track if we are in a wizard route
  private isCurrentlyOnWizardRoute = false;

  // Track if we're navigating to dashboard — only dashboard controls its own hide
  private navigatingToDashboard = false;
  private roleSwitchSubscription?: Subscription;
  private appLoadingSubscription?: Subscription;
  private hideTimer?: any;
  private failsafeTimer?: any;

  constructor(
    private router: Router,
    private roleService: RoleService,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.apiService.handlePageRefresh();
    this.initializeRoleSynchronization();
    this.updateSidenavState();

    // Route change loader
    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationStart) {
        const goingToDashboard = !!(event.url && event.url.includes('/dashboard'));
        const goingToReviewPage = !!(event.url && event.url.includes('/parkings/create/review'));
        const goingToWizardRoutes = !!(event.url && (
          event.url.includes('/parkings/create/') ||
          event.url.includes('/parking-add-people') ||
          event.url.includes('/parking-add-capacity') ||
          event.url.includes('/review-parking')
        ));
        this.navigatingToDashboard = goingToDashboard;
        
        // CRITICAL: Always clear any route-specific hiding attributes on navigation start
        document.body.removeAttribute('data-route');
        
        // Don't show loader on wizard routes including review page
        if (goingToWizardRoutes) {
          console.log('Wizard route detected - skipping global loader:', event.url);
          return;
        }
        
        // Don't show loader on regular navigation anymore
        // this.showOverlayLoader();

        // 🧪 TESTING: Timer to measure dashboard load time
        if (goingToDashboard) {
          (window as any).__dashboardNavStart = performance.now();
          console.log(`️ [Dashboard Timer] Navigation started → ${event.url}`);
        }
      } else if (event instanceof NavigationEnd) {
        this.updateSidenavState();
        
        // CRITICAL FIX: Check if we are ACTUALLY on the dashboard after all redirects.
        // If we intended to go to dashboard but ended up elsewhere (e.g. redirect to wizard),
        // we MUST hide the loader because only the dashboard hides its own loader.
        const currentUrl = event.urlAfterRedirects || event.url || '';
        const isFinallyDashboard = !!(currentUrl.includes('/dashboard'));
        const isFinallyReviewPage = !!(currentUrl.includes('/parkings/create/review'));
        
        // Force hide loader on review page (defense in depth)
        if (isFinallyReviewPage) {
          console.log('Force hiding loader on review page - NavigationEnd');
          document.body.setAttribute('data-route', 'review');
          this.navigatingToDashboard = false;
          this.isCurrentlyOnWizardRoute = true;
          this.hideOverlayLoaderImmediate();
          this.updateSidenavState(); // Ensure hideNavigation is synced
          return;
        }

        if (isFinallyDashboard) {
          console.log('Finally on dashboard - NavigationEnd');
          document.body.removeAttribute('data-route');
          this.navigatingToDashboard = true;
          this.updateSidenavState();
          return;
        }

        if (!isFinallyDashboard) {
          this.navigatingToDashboard = false;
          this.hideOverlayLoader();
        }
      } else if (event instanceof NavigationCancel || event instanceof NavigationError) {
        this.navigatingToDashboard = false;
        this.hideOverlayLoaderImmediate();
      }
    });

    // Loading text updates
    this.roleService.loadingText$.subscribe(text => {
      this.loadingText = text;
      this.cdr.detectChanges();
    });

    // Role switching state — but ignore hide if we're navigating to dashboard
    this.roleSwitchSubscription = this.roleService.isRoleSwitching$.pipe(skip(1)).subscribe(isSwitching => {
      if (isSwitching) {
        this.showOverlayLoader();
      } else if (!this.navigatingToDashboard) {
        this.hideOverlayLoader();
      }
    });

    // Generic app loading state from dashboard/other components
    this.appLoadingSubscription = this.roleService.isAppLoading$.pipe(skip(1)).subscribe(isLoading => {
      if (isLoading) {
        this.showOverlayLoader();
      } else {
        // Always hide when dashboard explicitly says it's ready
        this.navigatingToDashboard = false;
        this.hideOverlayLoaderImmediate();
      }
    });
  }

  private showOverlayLoader(): void {
    if (this.isLoadingOverlay) return;
    
    // FINAL DEFENSE: Never show global loader on wizard or review routes
    const currentUrl = this.router.url;
    
    const isWizard = currentUrl.includes('/parkings/create') || 
                    currentUrl.includes('/parking-add-people') ||
                    currentUrl.includes('/parking-add-capacity') ||
                    currentUrl.includes('/review-parking');

    if (isWizard) {
      console.log('showOverlayLoader BLOCKED - Wizard route detected:', { url: currentUrl });
      this.isCurrentlyOnWizardRoute = true;
      return;
    }

    this.isCurrentlyOnWizardRoute = false;
    console.log('Showing global loader overlay:', currentUrl);
    this.isLoadingOverlay = true;
    document.body.classList.add('parking-loader-active');
    
    // FAILSAFE: Automatically hide the loader after 15 seconds no matter what
    this.failsafeTimer = setTimeout(() => {
      if (this.isLoadingOverlay) {
        console.warn('️ Loader failsafe triggered after 15s. Force hiding loader.');
        this.hideOverlayLoaderImmediate();
      }
    }, 15000);
    
    this.cdr.detectChanges();
  }

  private hideOverlayLoader(): void {
    if (!this.isLoadingOverlay) return;
    if (this.hideTimer) return; // Already pending
    
    this.hideTimer = setTimeout(() => {
      this.hideOverlayLoaderImmediate();
    }, 100);
  }

  private hideOverlayLoaderImmediate(): void {
    this.isLoadingOverlay = false;
    document.body.classList.remove('parking-loader-active');
    this.clearTimers();
    this.cdr.detectChanges();
  }

  private clearTimers(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = undefined;
    }
    if (this.failsafeTimer) {
      clearTimeout(this.failsafeTimer);
      this.failsafeTimer = undefined;
    }
  }

  ngOnDestroy() {
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.roleSwitchSubscription?.unsubscribe();
    this.appLoadingSubscription?.unsubscribe();
  }

  private initializeRoleSynchronization(): void {
    const currentUser = this.apiService.getCurrentUser();
    // Only refresh role if user is authenticated
    if (currentUser && currentUser.id) {
      this.roleService.forceRefreshRole();
    }
  }

  updateSidenavState() {
    const currentUrl = this.router.url;
    const path = currentUrl.split('?')[0];

    const isAuthRoute = path.startsWith('/auth') || 
      path.startsWith('/login') || 
      path.startsWith('/signup') || 
      path.startsWith('/forgot-password') || 
      path.startsWith('/reset-password') ||
      path.startsWith('/network-error') ||
      path.includes('/login') ||
      path.includes('/auth/');
    const isPosRoute = path.startsWith('/pos') ||
      path.includes('/pos') && (path.endsWith('/pos') || path.includes('/pos-session') || path.includes('/pos-overview') || path.includes('/pos-dashboard') || path.includes('/pos-passes') || path.includes('/pos-facilities'));
    const isWizardRoute = path.startsWith('/parkings/create') ||
      path.startsWith('/parking-add-people') ||
      path.startsWith('/parking-add-capacity') ||
      path.startsWith('/review-parking') ||
      path.includes('/parkings/create') ||
      path.includes('/review-parking');
    const isNotFoundRoute = path.startsWith('/not-found') || path.includes('/not-found') || path.startsWith('/no-parking') || path.includes('/no-parking') || path.startsWith('/no-organization') || path.includes('/no-organization');

    this.isCurrentlyOnWizardRoute = isWizardRoute;
    const nativeLanding = this.nativeShell && (
      path === '/' || path === '' ||
      path === '/dashboard' || path.endsWith('/dashboard') ||
      path.includes('/setup-guide')
    );
    this.hideNavigation = isAuthRoute || isPosRoute || isWizardRoute || isNotFoundRoute || nativeLanding;
    
    if (this.hideNavigation) {
      // Force hide loader if we are on an excluded or auth route
      console.log('Force hiding loader - Auth/Wizard route detected');
      this.isLoadingOverlay = false;
      this.hideOverlayLoaderImmediate();
    }
    
    this.cdr.detectChanges();
  }

  /**
   * Template-level guard for the loader
   */
  public shouldShowLoader(): boolean {
    if (this.hideNavigation) return false;
    if (this.isCurrentlyOnWizardRoute) return false;
    
    // Extra safety: check URL again
    const url = this.router.url;
    if (url.includes('/parkings/create') || url.includes('/review-parking')) {
      return false;
    }
    
    return this.isLoadingOverlay;
  }
}
